import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '../services/authService';
import { LoginRequest, LoginResponse, ApiError } from '../types/api';
import { setCookie, getCookie, cookieKeys, clearAuthCookies } from '../utils/cookies';
import { hasFeaturePermission } from '../utils/rbac';
import { auditService } from '../services/auditService';
import { profileService } from '../services/profileService';
import { useAuditStore } from './auditStore';

interface AuthState {
  isAuthenticated: boolean;
  token: string | null;
  refreshToken: string | null;
  user: LoginResponse['user'] | null;
  role: string | null;
  permissions: string[];
  staffUserId: string | null;
  isLoading: boolean;
  error: ApiError | null;
  
  // Actions
  login: (credentials: LoginRequest & { rememberMe?: boolean }) => Promise<void>;
  logout: () => Promise<void>;
  setToken: (token: string, refreshToken?: string) => void;
  setUser: (user: LoginResponse['user']) => void;
  hasPermission: (permission: string) => boolean;
  clearError: () => void;
  checkAuth: () => void;
  fetchProfile: () => Promise<void>;
}

const getInitialToken = () => getCookie(cookieKeys.token);
const getInitialRefreshToken = () => getCookie(cookieKeys.refreshToken);

const DEFAULT_COOKIE_DAYS = 7;
const REMEMBER_ME_COOKIE_DAYS = 30;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => {
      const initialToken = getInitialToken();
      
      return {
        isAuthenticated: !!initialToken,
        token: initialToken,
        refreshToken: getInitialRefreshToken(),
        user: null,
        role: null,
        permissions: [],
        staffUserId: null,
        isLoading: false,
        error: null,

        login: async (credentials) => {
          set({ isLoading: true, error: null });
          const cookieDays = credentials.rememberMe ? REMEMBER_ME_COOKIE_DAYS : DEFAULT_COOKIE_DAYS;
          try {
            const response = await authService.login({
              email: credentials.email,
              password: credentials.password,
            });
            setCookie(cookieKeys.token, response.access_token, cookieDays);
            if (response.refreshToken) {
              setCookie(cookieKeys.refreshToken, response.refreshToken, cookieDays);
            }

            const staffUserId = response.user_id ?? response.user?.id ?? null;
            const user = response.user ?? {
              id: staffUserId ?? '',
              email: credentials.email,
            };
            if (staffUserId && !user.id) {
              user.id = staffUserId;
            }
            
            set({
              isAuthenticated: true,
              token: response.access_token,
              refreshToken: response.refreshToken || null,
              user,
              role: response.role ?? null,
              permissions: response.permissions ?? [],
              staffUserId,
              isLoading: false,
              error: null,
            });

            useAuditStore.getState().startSession();
          } catch (error) {
            clearAuthCookies();
            set({
              isAuthenticated: false,
              token: null,
              refreshToken: null,
              user: null,
              role: null,
              permissions: [],
              staffUserId: null,
              isLoading: false,
              error: error as ApiError,
            });
            throw error;
          }
        },

        logout: async () => {
          // Log logout before clearing state
          try {
            const auditState = useAuditStore.getState();
            const authState = get();
            const user = authState.user;
            const fullName = user?.name || [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'unknown';
            if (auditState.sessionId) {
              await auditService.create({
                sessionId: auditState.sessionId,
                adminId: user?.id ?? authState.staffUserId ?? 'unknown',
                adminName: fullName,
                adminRole: authState.role ?? 'unknown',
                adminEmail: user?.email ?? 'unknown',
                action: 'LOGOUT',
                module: 'auth',
                timestamp: new Date().toISOString(),
                url: window.location.pathname,
                userAgent: navigator.userAgent,
                status: 'success',
              });
            }
          } catch {
            // Never let audit logging block logout
          }

          try {
            await authService.logout();
          } catch (error) {
            console.error('Logout error:', error);
          } finally {
            useAuditStore.getState().endSession();
            clearAuthCookies();
            set({
              isAuthenticated: false,
              token: null,
              refreshToken: null,
              user: null,
              role: null,
              permissions: [],
              staffUserId: null,
              error: null,
            });
          }
        },

        setToken: (token: string, refreshToken?: string) => {
          setCookie(cookieKeys.token, token);
          if (refreshToken) {
            setCookie(cookieKeys.refreshToken, refreshToken);
          }
          set({
            token,
            refreshToken: refreshToken || null,
            isAuthenticated: !!token,
          });
        },

        setUser: (user: LoginResponse['user']) => {
          set({ user });
        },

        hasPermission: (permission: string) => {
          const { permissions, role } = get();
          if (permissions.includes(permission)) return true;
          return hasFeaturePermission(role, permission);
        },

        clearError: () => {
          set({ error: null });
        },

        checkAuth: () => {
          const token = getCookie(cookieKeys.token) || get().token;
          if (!token) {
            set({ isAuthenticated: false, user: null, token: null });
            return;
          }

          set({
            isAuthenticated: true,
            token,
            user: get().user,
          });
        },

        fetchProfile: async () => {
          try {
            const data = await profileService.getProfile();
            const currentUser = get().user;
            const baseUser = currentUser ?? {
              id: get().staffUserId ?? '',
              email: data.email,
            };

            set({
              user: {
                ...baseUser,
                name: data.fullName,
                email: data.email || baseUser.email,
                avatar: data.profilePictureUrl ?? undefined,
              },
            });
          } catch (error) {
            console.error('Failed to fetch profile:', error);
          }
        },
      };
    },
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        role: state.role,
        isAuthenticated: state.isAuthenticated,
        permissions: state.permissions,
        staffUserId: state.staffUserId,
      }),
    }
  )
);
